// store/features/ai-events/aiEventsSlice.ts

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  AIEventsState,
  AIGeneration,
  AITemplate,
  AISession,
  AIMessage,
  EventStructure,
} from '@/store/features/ai-events/aiEventsTypes';
import { aiEventsAPI } from './aiEventsAPI';

const initialState: AIEventsState = {

  navigationData: null,
  initialPrompt: null,
  generationId: null,
  // Generation state
  currentGeneration: null,
  isGenerating: false,
  generationError: null,

  // Event structure
  generatedEvent: null,
  savedEventId: null,

  // Template state
  selectedTemplate: null,
  availableTemplates: [],
  isLoadingTemplates: false,

  // Session state
  currentSession: null,
  sessions: [],
  messages: [],
  isLoadingMessages: false,

  // UI state
  showGenerationDialog: false,
  showTemplateSelector: false,
  generationProgress: 0,
};

const aiEventsSlice = createSlice({
  name: 'aiEvents',
  initialState,
  reducers: {
    // UI Actions
    setShowGenerationDialog: (state, action: PayloadAction<boolean>) => {
      state.showGenerationDialog = action.payload;
    },

    setShowTemplateSelector: (state, action: PayloadAction<boolean>) => {
      state.showTemplateSelector = action.payload;
    },

    setGenerationProgress: (state, action: PayloadAction<number>) => {
      state.generationProgress = action.payload;
    },

    // Generation Actions
    setGeneratedEvent: (state, action: PayloadAction<EventStructure>) => {

      state.generatedEvent = action.payload;
      state.isGenerating = false;
      state.generationError = null;
    },

    clearGeneratedEvent: (state) => {
      state.generatedEvent = null;
      state.savedEventId = null;
      state.currentGeneration = null;
      state.generationError = null;
      state.generationId = null;
    },

    // Template Actions
    setSelectedTemplate: (state, action: PayloadAction<AITemplate>) => {
      state.selectedTemplate = action.payload;
    },

    clearSelectedTemplate: (state) => {
      state.selectedTemplate = null;
    },

    // Session Actions
    setCurrentSession: (state, action: PayloadAction<AISession>) => {
      state.currentSession = action.payload;
    },

    clearCurrentSession: (state) => {
      state.currentSession = null;
      state.messages = [];
    },

    addMessage: (state, action: PayloadAction<AIMessage>) => {
      state.messages.push(action.payload);
    },

    clearMessages: (state) => {
      state.messages = [];
    },


    clearError: (state) => {
      state.generationError = null;
    },

    // Set generation ID
    setGenerationId: (state, action: PayloadAction<string>) => {
      state.generationId = action.payload;
    },

    // Set generating state
    setIsGenerating: (state, action: PayloadAction<boolean>) => {
      state.isGenerating = action.payload;
    },

    // Set error
    setGenerationError: (state, action: PayloadAction<string | null>) => {
      state.generationError = action.payload;
      state.isGenerating = false;
    },

    setNavigationData: (state, action: PayloadAction<any>) => {
      state.navigationData = action.payload;
    },

    // ✅ ADD: Clear initial state after session starts
    clearInitialData: (state) => {
      state.initialPrompt = null;
      state.navigationData = null;
    },

    // NEW: Clear navigation data
    clearNavigationData: (state) => {
      state.navigationData = null;
    },

    // NEW: Set initial prompt for editor
    setInitialPrompt: (state, action: PayloadAction<string>) => {
      state.initialPrompt = action.payload;
    },
  },

  extraReducers: (builder) => {
    // ===== GENERATE EVENT =====
    builder.addMatcher(
      aiEventsAPI.endpoints.generateEvent.matchPending,
      (state) => {
        state.isGenerating = true;
        state.generationError = null;
        state.generationProgress = 10;
      }
    );
    builder.addMatcher(
      aiEventsAPI.endpoints.generateEvent.matchFulfilled,
      (state, action) => {
        state.isGenerating = false;
        state.generatedEvent = {
          ...action.payload.event,
          // Map pagesections to page_sections if backend returns wrong name
          page_sections: action.payload.event.page_sections || []
        };
        state.currentGeneration = action.payload.ai_generation;
        state.savedEventId = action.payload.saved_event?.documentId || null;
        state.generationProgress = 100;
        state.generationError = null;
      }
    );
    builder.addMatcher(
      aiEventsAPI.endpoints.generateEvent.matchRejected,
      (state, action: any) => {
        state.isGenerating = false;
        state.generationError =
          action.payload?.data?.error ||
          action.payload?.data?.message ||
          'Failed to generate event';
        state.generationProgress = 0;
      }
    );

    // ===== GENERATE TEMPLATE =====
    builder.addMatcher(
      aiEventsAPI.endpoints.generateTemplateStructure.matchPending,
      (state) => {
        state.isLoadingTemplates = true;
        state.generationError = null;
      }
    );
    builder.addMatcher(
      aiEventsAPI.endpoints.generateTemplateStructure.matchFulfilled,
      (state, action) => {
        state.isLoadingTemplates = false;
        state.selectedTemplate = action.payload.template;
        state.generatedEvent = action.payload.structure;
        state.generationError = null;
      }
    );
    builder.addMatcher(
      aiEventsAPI.endpoints.generateTemplateStructure.matchRejected,
      (state, action: any) => {
        state.isLoadingTemplates = false;
        state.generationError =
          action.payload?.data?.error ||
          'Failed to generate template';
      }
    );

    // ===== GET AI TEMPLATES =====
    builder.addMatcher(
      aiEventsAPI.endpoints.getAITemplates.matchFulfilled,
      (state, action) => {
        state.availableTemplates = action.payload.data;
      }
    );

    // ===== GET AI SESSIONS =====
    builder.addMatcher(
      aiEventsAPI.endpoints.getAISessions.matchFulfilled,
      (state, action) => {
        state.sessions = action.payload.data;
      }
    );

    // ===== GET SESSION MESSAGES =====
    builder.addMatcher(
      aiEventsAPI.endpoints.getSessionMessages.matchPending,
      (state) => {
        state.isLoadingMessages = true;
      }
    );
    builder.addMatcher(
      aiEventsAPI.endpoints.getSessionMessages.matchFulfilled,
      (state, action) => {
        state.isLoadingMessages = false;
        state.messages = action.payload.data;
      }
    );
    builder.addMatcher(
      aiEventsAPI.endpoints.getSessionMessages.matchRejected,
      (state) => {
        state.isLoadingMessages = false;
      }
    );

    // ===== CREATE AI SESSION =====
    builder.addMatcher(
      aiEventsAPI.endpoints.createAISession.matchFulfilled,
      (state, action) => {
        state.currentSession = action.payload;
        state.sessions.push(action.payload);
      }
    );

    // ===== SEND AI MESSAGE =====
    builder.addMatcher(
      aiEventsAPI.endpoints.sendAIMessage.matchFulfilled,
      (state, action) => {
        state.messages.push(action.payload);
      }
    );

    // ===== DELETE AI SESSION =====
    builder.addMatcher(
      aiEventsAPI.endpoints.deleteAISession.matchFulfilled,
      (state, action) => {
        const sessionId = action.meta.arg.originalArgs;
        state.sessions = state.sessions.filter(s => s.documentId !== sessionId);
        if (state.currentSession?.documentId === sessionId) {
          state.currentSession = null;
          state.messages = [];
        }
      }
    );
  },
});

export const {
  setShowGenerationDialog,
  setShowTemplateSelector,
  setGenerationProgress,
  setGeneratedEvent,
  clearGeneratedEvent,
  setSelectedTemplate,
  clearSelectedTemplate,
  setCurrentSession,
  clearCurrentSession,
  addMessage,
  clearMessages,
  setGenerationId,
  setIsGenerating,
  setGenerationError,
  clearError,
  setInitialPrompt,
  setNavigationData,
  clearInitialData
} = aiEventsSlice.actions;

export default aiEventsSlice.reducer;
